import * as api from '@/api/reolink/serverApi/common/activity';
import type { IButton, IGetThemeListResponse } from '@/interface/ICommon';
import { defaultButtonConfig } from './const';
import type { IBanner, IBannerDefaultConfig, IBannerGroup, IOwlBannerFetchType } from './IComponent';

export const fetch = async ({ bannerConfig, country, lang, count }: IOwlBannerFetchType): Promise<IBannerGroup> => {

    const res = await api.getActivityThemeList({ country, lang, page: 'store' });
    return {
        // 因为从多站点传进来的数字必定是string，所以需要格式化当前string，并且设置默认值为5个
        bannerConfig: [
            ...formatFetchBannerConfig(res.items),
            ...formatDefaultBannerConfig(bannerConfig)
        ].slice(0, formatStringIntoNumberWithDefault(5, count))
    };
};

// 将字符串转化为数字，若数字为NaN就使用默认的值
const formatStringIntoNumberWithDefault = (defaultNum: number, num?: string): number => {
    return num && !isNaN(Number(num)) ? Number(num) : defaultNum;
};

// 格式化默认banner数据
const formatDefaultBannerConfig = (bannerConfig: IBannerDefaultConfig[]): IBanner[] =>
// 去掉无效banner
    bannerConfig.filter(banner => banner.title || banner.imagePc.src || banner.videoPc).map(banner => ({
        images: {
            'store-mobile': banner.imageMobile.src ?? '',
            'store-pc': banner.imagePc.src ?? ''
        },
        videos: {
            'store-mobile': banner.videoMobile ?? '',
            'store-pc': banner.videoPc ?? ''
        },
        meta: {
            title: banner.title ?? '',
            description: banner.description ?? ''
        },
        setting: {
            mobileIsSame: true,
            pc: {
                button: banner.button?.map(button => ({
                    backgroundColor: button.backgroundColor ?? defaultButtonConfig.backgroundColor,
                    color: button.color ?? defaultButtonConfig.btnColor,
                    text: button.text,
                    link: button.link,
                })) ?? [],
                description: {
                    text: banner.description ?? '',
                    color: banner.color ?? defaultButtonConfig.bannerColor,
                    param: '',
                    paramColor: ''
                },
                note: {
                    text: banner.note ?? '',
                    color: banner.color ?? defaultButtonConfig.bannerColor,
                    param: '',
                    paramColor: ''
                },
                title: {
                    text: banner.title ?? '',
                    color: banner.color ?? defaultButtonConfig.bannerColor,
                    param: '',
                    paramColor: ''
                },
                link: banner.link ?? ''
            },
            mobile: {
                button: banner.button?.map(button => ({
                    backgroundColor: button.backgroundColor ?? defaultButtonConfig.backgroundColor,
                    color: button.color ?? defaultButtonConfig.btnColor,
                    text: button.text,
                    link: button.link,
                })) ?? [],
                description: {
                    text: banner.description ?? '',
                    color: banner.color ?? defaultButtonConfig.bannerColor,
                    param: '',
                    paramColor: ''
                },
                note: {
                    text: banner.note ?? '',
                    color: banner.color ?? defaultButtonConfig.bannerColor,
                    param: '',
                    paramColor: ''
                },
                title: {
                    text: banner.title ?? '',
                    color: banner.color ?? defaultButtonConfig.bannerColor,
                    param: '',
                    paramColor: ''
                },
                link: banner.link ?? ''
            }
        }
    }));

// 格式化接口返回的banner数据
const formatFetchBannerConfig = (bannerConfig: IGetThemeListResponse['items']): IBanner[] =>
    bannerConfig.map(banner => {
        const defaultPc = banner.defaultSetting.pc;
        const defaultM = (banner.defaultSetting.mobileIsSame ?? true) ? defaultPc : banner.defaultSetting.mobile;
        const pagePc = banner.pageSetting.pc;
        const pageM = banner.pageSetting.mobile;
        return {
            images: banner.images,
            videos: banner.videos,
            meta: banner.meta,
            setting: {
                mobileIsSame: banner.pageSetting?.mobileIsSame ?? true,
                mobile: {
                    button: pageM.button.map(mergeButtonConfig(defaultM.button[0])), // 封装一下，减少重复代码
                    description: mergeObjVal(pageM.description, defaultM.description),
                    note: mergeObjVal(pageM.note, defaultM.note),
                    title: mergeObjVal(pageM.title, defaultM.title),
                    link: pageM.link || defaultM.link
                },
                pc: {
                    button: pagePc.button.map(mergeButtonConfig(defaultPc.button[0])),
                    description: mergeObjVal(pagePc.description, defaultPc.description),
                    note: mergeObjVal(pagePc.note, defaultPc.note),
                    title: mergeObjVal(pagePc.title, defaultPc.title),
                    link: pagePc.link || defaultPc.link
                }
            }
        };
    });

// 以第一个对象为基准合并对象，优先选用第一个对象的值
const mergeObjVal = <T>(target: T, source: T ): T => {
    for (const key in target) {
        target[key] = target[key] || source[key];
    }
    return target;
};

// 合并两个按钮组的值
const mergeButtonConfig = (defaultButton: IButton) => (pageButton: IButton): IButton => {
    return {
        backgroundColor: pageButton.backgroundColor || defaultButton.backgroundColor || defaultButtonConfig.backgroundColor,
        color: pageButton.color || defaultButton.color || defaultButtonConfig.btnColor,
        link: pageButton.link || defaultButton.link,
        text: pageButton.text || defaultButton.text
    };
};
